"use client";

import { useMemo, useState } from "react";
import type { ReactNode } from "react";
import Link from "next/link";
import { getSiteContent } from "@/content";
import { type Locale, localizedPath } from "@/lib/site";
import { FieldInput, FieldSelect, FieldTextarea } from "@/components/ui/StitchUI";

export function ParcelPrealert({ locale }: { locale: Locale }) {
  const t = getSiteContent(locale).pages.prealert;
  const [category, setCategory] = useState<string>(t.categories[0]);
  const [selectedAddOns, setSelectedAddOns] = useState<string[]>(() => [...t.defaultAddOns]);
  const [submitted, setSubmitted] = useState(false);

  const serviceFee = useMemo(() => t.addOns.filter((item) => selectedAddOns.includes(item.name)).reduce((sum, item) => sum + item.fee, 0), [selectedAddOns, t.addOns]);

  function toggleAddOn(name: string) {
    setSelectedAddOns((current) => (current.includes(name) ? current.filter((item) => item !== name) : [...current, name]));
  }

  return (
    <main className="mx-auto max-w-container-max px-margin-mobile pb-stack-lg pt-[156px] md:px-margin-desktop md:pt-[168px]">
      <header className="mb-stack-lg">
        <h1 className="typo-page-h1 mb-2 text-primary">{t.title}</h1>
        <p className="max-w-2xl font-body-md text-body-md text-on-surface-variant">{t.subtitle}</p>
      </header>

      <div className="glass-card prealert-steps-shell mb-gutter rounded-panel p-stack-lg">
        <div className="prealert-steps-track">
          {t.steps.map((step, index) => (
            <div className="prealert-step flex flex-1 items-center last:flex-none" key={step}>
              <div className="flex flex-col items-center">
                <div className={`flex h-6 w-6 items-center justify-center rounded-full text-label-sm font-bold ${index === 0 ? "bg-parcel-orange text-white" : "bg-surface-container-highest text-on-surface-variant"}`}>{index + 1}</div>
                <span className={`prealert-step-label mt-2 whitespace-nowrap font-label-md ${index === 0 ? "text-parcel-orange" : "text-on-surface-variant opacity-60"}`}>{step}</span>
              </div>
              {index < 2 ? <div className="step-line" /> : null}
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-gutter lg:grid-cols-3">
        <div className="flex flex-col gap-gutter lg:col-span-2">
          <section className="rounded-panel border border-border-pale bg-white p-stack-lg shadow-sm">
            <div className="mb-stack-lg flex items-center gap-2 text-secondary">
              <span className="material-symbols-outlined">package_2</span>
              <h2 className="font-headline-md text-headline-md">{t.coreTitle}</h2>
            </div>
            <div className="grid grid-cols-1 gap-stack-lg md:grid-cols-2">
              <FieldShell label={t.fields.courier}>
                <div className="relative">
                  <FieldSelect className="h-12 appearance-none bg-surface-container-lowest pr-10">
                    {t.couriers.map((courier) => (
                      <option key={courier}>{courier}</option>
                    ))}
                  </FieldSelect>
                  <span className="material-symbols-outlined pointer-events-none absolute right-3 top-3 text-on-surface-variant">expand_more</span>
                </div>
              </FieldShell>
              <FieldShell label={t.fields.trackingNo}>
                <FieldInput className="h-12 bg-surface-container-lowest" placeholder={t.fields.trackingPlaceholder} type="text" />
              </FieldShell>
              <FieldShell label={t.fields.itemName}>
                <FieldInput className="h-12 bg-surface-container-lowest" placeholder={t.fields.itemPlaceholder} type="text" />
              </FieldShell>
              <div className="grid grid-cols-2 gap-4">
                <FieldShell label={t.fields.quantity}>
                  <FieldInput className="h-12 bg-surface-container-lowest" placeholder={t.fields.quantityPlaceholder} type="number" />
                </FieldShell>
                <FieldShell label={t.fields.arrival}>
                  <FieldInput className="h-12 bg-surface-container-lowest" type="date" />
                </FieldShell>
              </div>
              <div className="flex flex-col gap-2 md:col-span-2">
                <label className="font-label-md text-on-surface-variant">{t.fields.category}</label>
                <div className="flex flex-wrap gap-2">
                  {t.categories.map((item) => (
                    <button
                      className={`rounded-full border px-4 py-2 font-label-md transition-colors hover:border-secondary ${
                        category === item ? "border-secondary bg-secondary-container/10 text-secondary" : "border-border-pale bg-surface-container-lowest text-on-surface-variant"
                      }`}
                      key={item}
                      onClick={() => setCategory(item)}
                      type="button"
                    >
                      {item}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className="rounded-panel border border-border-pale bg-white p-stack-lg shadow-sm">
            <div className="mb-stack-lg flex items-center gap-2 text-secondary">
              <span className="material-symbols-outlined">add_moderator</span>
              <h2 className="font-headline-md text-headline-md">{t.addOnsTitle}</h2>
            </div>
            <div className="mb-stack-lg grid grid-cols-1 gap-stack-md sm:grid-cols-3">
              {t.addOns.map(({ icon, name, price }) => {
                const checked = selectedAddOns.includes(name);
                return (
                  <label className={`group relative flex cursor-pointer flex-col items-center rounded-xl border p-4 transition-all active:scale-95 ${checked ? "border-parcel-orange bg-parcel-orange/5" : "border-border-pale bg-surface-container-lowest hover:border-parcel-orange"}`} key={name}>
                    <input checked={checked} className="absolute right-3 top-3 h-4 w-4 rounded border-border-pale text-parcel-orange focus:outline-none focus:ring-0" onChange={() => toggleAddOn(name)} type="checkbox" />
                    <span className={`material-symbols-outlined mb-2 text-4xl ${checked ? "text-parcel-orange" : "text-on-surface-variant group-hover:text-parcel-orange"}`}>{icon}</span>
                    <span className="font-label-md text-primary">{name}</span>
                    <span className={`typo-status-tag mt-1 ${checked ? "text-parcel-orange" : "text-on-surface-variant"}`}>{price}</span>
                  </label>
                );
              })}
            </div>
            <FieldShell label={t.noteLabel}>
              <FieldTextarea className="resize-none bg-surface-container-lowest" placeholder={t.notePlaceholder} rows={3} />
            </FieldShell>
          </section>
        </div>

        <aside className="flex flex-col gap-gutter">
          <section className="ambient-deep-bg relative overflow-hidden rounded-panel bg-primary-container p-stack-lg text-white">
            <div className="relative z-10">
              <h3 className="mb-4 font-headline-md text-white">{t.helper.title}</h3>
              <ul className="space-y-4">
                {t.helper.items.map(([icon, copy]) => (
                  <HelperItem icon={icon} key={copy}>{copy}</HelperItem>
                ))}
              </ul>
            </div>
            <div className="absolute -bottom-10 -right-10 opacity-10">
              <span className="material-symbols-outlined !text-[160px]">package_2</span>
            </div>
          </section>

          <section className="flex flex-col gap-4 rounded-panel border border-border-pale bg-white p-stack-lg shadow-lg">
            <SummaryRow label={t.summary.packageCountLabel} value={t.summary.packageCountValue} />
            <SummaryRow label={t.summary.serviceFeeLabel} value={`￥${serviceFee.toFixed(2)}`} highlight />
            <div className="py-4">
              <button className="w-full rounded-panel bg-parcel-orange py-4 font-headline-md text-white shadow-lg shadow-parcel-orange/20 transition-all hover:bg-[#E67C00] active:scale-[0.98]" onClick={() => setSubmitted(true)} type="button">
                {t.summary.submit}
              </button>
              <p className="typo-status-tag mt-4 text-center text-on-surface-variant">
                {t.summary.agreementPrefix}
                <Link className="text-secondary hover:underline" href={localizedPath(locale, "legal/service-agreement")}>{t.summary.serviceAgreement}</Link>
              </p>
              {submitted ? <p className="typo-status-tag mt-3 rounded-xl bg-success/10 px-4 py-3 text-center text-success">{t.summary.submitted}</p> : null}
            </div>
          </section>

          <section className="relative h-48 overflow-hidden rounded-panel border border-border-pale">
            <div className="h-full w-full bg-cover bg-center" style={{ backgroundImage: "url('/assets/images/prealert-warehouse-stitch.png')" }} />
            <div className="absolute inset-0 flex items-end bg-gradient-to-t from-primary/60 to-transparent p-6">
              <span className="font-label-md text-white">{t.summary.visualLabel}</span>
            </div>
          </section>
        </aside>
      </div>
    </main>
  );
}

function FieldShell({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <label className="font-label-md text-on-surface-variant">{label}</label>
      {children}
    </div>
  );
}

function HelperItem({ icon, children }: { icon: string; children: ReactNode }) {
  return (
    <li className="flex gap-3">
      <span className="material-symbols-outlined text-tertiary-fixed-dim">{icon}</span>
      <p className="text-body-sm opacity-90">{children}</p>
    </li>
  );
}

function SummaryRow({ label, value, highlight = false }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className="flex items-center justify-between border-b border-surface-container py-2">
      <span className="font-body-sm text-on-surface-variant">{label}</span>
      <span className={`font-bold ${highlight ? "text-secondary" : "text-primary"}`}>{value}</span>
    </div>
  );
}
